import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import { styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createContext, FC, forwardRef, HTMLAttributes, ReactElement, ReactNode, useContext, useMemo } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { Lot } from '../api/dtos';

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    const dataSet = data[index];
    const inlineStyle = {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING,
    };

    const { key, ...optionProps } = dataSet[0];

    return (
        <Typography key={key} component="li" {...optionProps} noWrap style={inlineStyle}>
            {(dataSet[1] as Lot).number}
        </Typography>
    );
}

const OuterElementContext = createContext({});

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

// function useResetCache(data: any) {
//     const ref = useRef<VariableSizeList>(null);
//     useEffect(() => {
//         if (ref.current != null) {
//             ref.current.resetAfterIndex(0, true);
//         }
//     }, [data]);
//     return ref;
// }

// Adapter for react-window
const ListboxComponent = forwardRef<
    HTMLDivElement,
    HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
    const { children, ...other } = props;

    const itemData = useMemo(() => {
        const output: ReactElement<unknown>[] = [];
        (children as ReactElement<unknown>[]).forEach(
            (
                item: ReactElement<unknown> & {
                    children?: ReactElement<unknown>[];
                },
            ) => {
                output.push(item);
                output.push(...(item.children || []));
            },
        );
        
        return output;
    }, [children])

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
        noSsr: true,
    });

    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (_child: ReactElement<unknown>) => {
        return itemSize;
    };

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    // const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <FixedSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    //ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={40}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </FixedSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
    },
});

interface IVirtualizedDataSelectProps<TData> {
    onChange: (params: any) => any,
    data: TData[]
}

export const VirtualizedLotSelect:FC<IVirtualizedDataSelectProps<Lot>> = ({onChange, data}) => {
    return (
        <Autocomplete
            onChange={(_event, value) => onChange(value.map(lot => lot.id))}
            fullWidth 
            multiple
            disableListWrap
            disableCloseOnSelect
            options={data}
            filterOptions={(options, state) => state.inputValue ? options.filter(x => x.number?.toString().includes(state.inputValue)) : options}
            renderInput={(params) => <TextField {...params} label="Lots" />}
            renderValue={(value) => value.map(x => x.number).join(', ')}
            renderOption={(props, option, state) => 
                [props, option, state.index] as ReactNode
            }
            slots={{
                popper: StyledPopper,
            }}
            slotProps={{
                listbox: {
                    component: ListboxComponent,
                },
            }}
        />
    );
}
